import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { replaceClip } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { isUsableFootage, type FootageView } from '../footage/footageView';
import { useSectionBankClips } from '../footage/useSectionBankClips';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { footageMessages } from '../messages/footageMessages';
import { panelMessages } from '../messages/panelMessages';
import { ClipCard } from '../panels/footage/ClipCard';
import { ShowcaseSearchBusy } from '../showcase/ShowcaseSearchBusy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneClipPickerProps {
  section: MusicVideoSongMapSection;
  clipId: string;
  onDone: () => void;
}

/**
 * "Swap this clip" on a phone (design f2): the clips of the bank for the section and the own clips;
 * a tap gives the clip this footage and keeps its length. Uploads stay on the wide editor.
 */
const PhoneClipPickerComponent: React.FC<PhoneClipPickerProps> = ({ section, clipId, onDone }) => {
  const intl = useIntl();
  const { footage, change } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  const suggest = useSectionBankClips(section);
  const bank = suggest.views;
  const own = useMemo(() => footage.own.filter(isUsableFootage), [footage.own]);
  const pick = (view: FootageView) => {
    change((doc) => replaceClip(doc, clipId, view.source, limitsFor(view)));
    onDone();
  };
  return (
    <>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(panelMessages.phonePick)}</b>
        <span className={styles.grow} />
        <button type="button" className={styles.linkButton} onClick={onDone}>
          {intl.formatMessage(panelMessages.phoneClose)}
        </button>
      </div>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(footageMessages.forSection, { section: section.label })}</b>
      </div>
      {suggest.isBusy && <ShowcaseSearchBusy />}
      {suggest.isLoading ? (
        <LoadingView small />
      ) : (
        <div className={styles.clipGrid}>
          {bank.map((view) => (
            <ClipCard key={view.key} view={view} onPick={pick} />
          ))}
        </div>
      )}
      {own.length > 0 && (
        <>
          <div className={styles.sectionHead}>
            <b>{intl.formatMessage(footageMessages.yoursHeading)}</b>
          </div>
          <div className={styles.clipGrid}>
            {own.map((view) => (
              <ClipCard key={view.key} view={view} showSource onPick={pick} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export const PhoneClipPicker = TypedMemo(PhoneClipPickerComponent);
